// ---------------------------------------------------------------------------
// "Como pagar" — UMA funcao para todas as telas que mostram ao aluno como pagar
// (QR do Pix, Financeiro do aluno, "Cobrar no WhatsApp" do professor).
// Fase 0, Etapa 3 (02/10).
//
// Hoje todo acordo e' Pix direto na chave do professor (contratos.forma_recebimento =
// 'pix_direto'): o dinheiro vai do aluno para o professor e a plataforma so' formata o QR.
// Na Fase 3, um acordo com forma_recebimento = 'plen' vai devolver o link de pagamento da
// Plen — as telas nao mudam, so' esta funcao. Esse ramo existe mas NAO esta' implementado.
//
// Regras do valor:
//  * com cobranca(s) EM ABERTO: a soma do que esta' em aberto (nao o preco do acordo — um
//    desconto de reposicao ou um valor corrigido na cobranca tem de aparecer no QR);
//  * sem cobranca em aberto ("Pagar adiantado"): mensalidade -> valor_mensal (o que a rotina
//    cobra); pacote -> preco_negociado (o valor do proximo pacote).
// txid (codigo que vai dentro do QR): 'PLEN' + id da cobranca (uma cobranca) ou + id do aluno
// (varias), so' letras e numeros, ate' 25. Em Pix estatico ele nem sempre aparece no extrato
// do professor: ajuda a conferir, nao garante conciliacao.
//
// Modulo puro, formato da API: sem React, sem estado, sem rede.
// ---------------------------------------------------------------------------
import { gerarBRCode, limparTxid } from './pix.js';

export const FORMA_PIX_DIRETO = 'pix_direto';
export const FORMA_PLEN = 'plen';

const centavos = v => Math.round((Number(v) || 0) * 100);

export function cobrancasEmAberto(cobrancas) {
  return (cobrancas || []).filter(c => c && c.status === 'pendente');
}

// Soma em centavos (sem erro de virgula flutuante: 0,1 + 0,2).
export function valorEmAberto(cobrancas) {
  return cobrancasEmAberto(cobrancas).reduce((t, c) => t + centavos(c.valor), 0) / 100;
}

// Valor do proximo pagamento quando nao ha' cobranca em aberto.
export function valorDoProximo(contrato) {
  if (!contrato) return 0;
  if (contrato.tipo_cobranca === 'mensal') return Number(contrato.valor_mensal) > 0 ? Number(contrato.valor_mensal) : 0;
  return Number(contrato.preco_negociado) > 0 ? Number(contrato.preco_negociado) : 0;
}

function semHifen(id) { return String(id || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase(); }

export function txidDe(abertas, contrato) {
  if (abertas.length === 1 && abertas[0].id) return limparTxid('PLEN' + semHifen(abertas[0].id));
  if (abertas.length > 1) return limparTxid('PLEN' + semHifen(abertas[0].aluno_id || (contrato && contrato.aluno_id) || ''));
  return limparTxid('PLEN' + semHifen(contrato && contrato.id));
}

/**
 * @param {Array}  cobrancas — linhas de pagamentos do aluno (so' as 'pendente' contam)
 * @param {object} professor — { nome, pix_chave, pix_tipo }
 * @param {object} contrato  — o acordo ativo { id, tipo_cobranca, valor_mensal, preco_negociado, forma_recebimento }
 * @returns {{forma:'plen'} | {forma:'pix_direto', chave, tipo_chave, brcode, valor, txid, em_aberto, quantidade, erro}}
 */
export function comoPagar(cobrancas, professor, contrato) {
  const forma = contrato && contrato.forma_recebimento === FORMA_PLEN ? FORMA_PLEN : FORMA_PIX_DIRETO;
  if (forma === FORMA_PLEN) return { forma: FORMA_PLEN };   // Fase 3: link de pagamento da Plen (ainda nao existe)

  const abertas = cobrancasEmAberto(cobrancas);
  const valor = abertas.length ? valorEmAberto(abertas) : valorDoProximo(contrato);
  const chave = professor && professor.pix_chave ? String(professor.pix_chave).trim() : '';
  const txid = txidDe(abertas, contrato);
  const base = {
    forma: FORMA_PIX_DIRETO, chave, tipo_chave: (professor && professor.pix_tipo) || null,
    valor, txid, em_aberto: abertas.length > 0, quantidade: abertas.length,
  };
  if (!chave) return { ...base, brcode: null, erro: 'sem_chave' };
  const r = gerarBRCode({ chave, nome: (professor && professor.nome) || 'PROFESSOR', valor: valor > 0 ? valor : undefined, txid });
  return { ...base, brcode: r.payload || null, erro: r.payload ? null : 'sem_chave' };
}
